import * as React from 'react'
import { ChevronDown, Image, Loader2, X, Zap } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
// Tooltip imports removed — video style picker moved to StepIdea
import { cn } from '@/lib/utils'
import { HelpHint } from '@/components/ui/help-hint'
// VIDEO_MODE_CONFIG moved to StepIdea (per-scene style picker)
import { apiJson } from '@/lib/api'
import type { Asset, AssetFolder, AspectRatio, ReferenceVideo, VideoModel, UserParamDef } from '@/types'

export type GenerationMode = 'product' | 'avatar'

export type VideoMode = 'ugc' | 'cinematic_product' | 'lifestyle' | 'hype_retail'

export interface SetupValues {
  avatarAssetId: number | null
  /** All reference image asset IDs selected in StepIdea (up to 3). First item matches avatarAssetId. */
  refImageIds?: number[]
  contextFolderIds: number[]
  styleReferenceIds: number[]
  modelId: string
  aspectRatio: AspectRatio
  generationMode: GenerationMode
  /** Three-way type from StepIdea — used for model filtering */
  videoType?: 'product' | 'avatar' | 'composite'
  /** Visual style driving how the script generator writes Seedance / OmniHuman prompts */
  videoMode: VideoMode
  /** Auto-stitch all scenes into one final video once generation completes
   *  (Task 1: multi-scene fix). Default ON for wizard-created assemblies. */
  autoStitch?: boolean
  /** Crossfade duration in seconds between scenes at stitch time. 0 = hard
   *  cuts. Default 0.3s reads as imperceptible smoothing while absorbing
   *  the small visual delta between bridge-anchored adjacent frames. */
  transitionSeconds?: number
  /** Task 8: fast preview mode. When true, the backend swaps the chosen
   *  model to its fast variant (where one exists), forces 480p resolution,
   *  caps every scene to 5s, and skips auto-stitch. Roughly 30-60s per
   *  scene at ~half the per-second cost. Default OFF — final renders use
   *  the model + resolution + durations the user actually picked. */
  fastPreview?: boolean
  /** Bridge toggle — when ON every scene chains to the previous scene's last
   *  frame to lock identity, outfit, and setting. Default OFF. */
  bridgeEnabled?: boolean
  audioUrl: string
  voiceId: string
  durationSeconds: number
  generationParams: Record<string, any>
}

/** Map the wizard's videoMode to a backend style_profile id. The assembly
 *  endpoint applies this profile to every scene unless that scene has an
 *  explicit per-scene override. */
export const VIDEO_MODE_TO_FILMOGRAPHY: Record<VideoMode, string> = {
  ugc: 'ugc_authentic',
  cinematic_product: 'cinematic_high_product',
  lifestyle: 'editorial_lifestyle',
  hype_retail: 'hype_retail',
}

/** Task 8: client-side mirror of backend FAST_VARIANT_OF keys. Used by the
 *  fast-preview cost-savings copy — without a fast variant the model_id
 *  stays the same and the per-second cost doesn't drop, so the "roughly
 *  half cost" message would be misleading. Keep this set in lockstep with
 *  app/core/video_models.py FAST_VARIANT_OF when adding/removing variants. */
export const MODELS_WITH_FAST_VARIANT: ReadonlySet<string> = new Set([
  'bytedance/seedance-2.0/text-to-video',
  'bytedance/seedance-2.0/reference-to-video',
])

interface Props {
  values: SetupValues
  onChange: (next: SetupValues) => void
  onNext: () => void
}

/** Infer model category when the backend hasn't shipped the `category` field yet. */
function inferIsAvatar(model: VideoModel): boolean {
  if (model.category === 'avatar' || model.category === 'lipsync') return true
  if (model.category !== undefined) return false
  // Fallback: check id / name strings
  const haystack = `${model.id} ${model.name}`.toLowerCase()
  return (
    haystack.includes('avatar') ||
    haystack.includes('omnihuman') ||
    haystack.includes('heygen') ||
    haystack.includes('lipsync')
  )
}

/** Filter models by the three-way video type using video_types when available,
 *  falling back to inferIsAvatar() for older catalog responses. */
function filterModelsByVideoType(
  models: VideoModel[],
  videoType: 'product' | 'avatar' | 'composite',
): VideoModel[] {
  return models.filter((m) => {
    if (m.mode === 'text-to-image' || m.mode === 'image-to-image') return false
    // If the catalog supplies video_types, use them directly
    if (m.video_types && m.video_types.length > 0) {
      return m.video_types.includes(videoType)
    }
    // Legacy fallback: infer from category/id
    const isAv = inferIsAvatar(m)
    if (videoType === 'avatar') return isAv
    if (videoType === 'composite') return true // composite: show all video models
    return !isAv // product
  })
}

/** Build default generationParams from a model's user_params definitions. */
function buildDefaultParams(model: VideoModel): Record<string, any> {
  if (!model.user_params) return {}
  return Object.fromEntries(
    Object.entries(model.user_params).map(([k, v]) => [k, v.default ?? null]),
  )
}

export function StepSetup({ values, onChange, onNext }: Props) {
  const [models, setModels] = React.useState<VideoModel[]>([])
  const [folders, setFolders] = React.useState<AssetFolder[]>([])
  const [references, setReferences] = React.useState<ReferenceVideo[]>([])
  const [assets, setAssets] = React.useState<Asset[]>([])
  const [voices, setVoices] = React.useState<{ id: string; name: string; gender: string; style: string }[]>([])
  const [loading, setLoading] = React.useState(true)
  const [showRefImagePicker, setShowRefImagePicker] = React.useState(false)
  const [showOtherModels, setShowOtherModels] = React.useState(false)

  // Avatar mode = explicitly set by generationMode, NOT derived from avatarAssetId.
  // avatarAssetId can be set for product-mode reference images too — don't conflate the two.
  const isAvatarMode = values.generationMode === 'avatar'
  // hasRefImage = product mode with a reference image selected (show read-only thumbnail, no voice)
  const hasRefImage = !isAvatarMode && values.avatarAssetId !== null

  React.useEffect(() => {
    Promise.all([
      apiJson<{ items: VideoModel[] }>('/api/v1/models').catch(() => ({ items: [] })),
      apiJson<{ items: AssetFolder[]; total: number }>('/api/v1/folders').catch(() => ({
        items: [],
        total: 0,
      })),
      apiJson<{ items: ReferenceVideo[]; total: number }>('/api/v1/references').catch(
        () => ({ items: [], total: 0 }),
      ),
      apiJson<{ items: Asset[]; total: number }>('/api/v1/assets/search?limit=200').catch(() => ({
        items: [],
        total: 0,
      })),
    ]).then(([modelsData, foldersData, refsData, assetsData]) => {
      setModels(modelsData.items)
      setFolders(foldersData.items)
      setReferences(refsData.items)
      setAssets(assetsData.items)

      // Fetch voices (non-blocking)
      apiJson<{ voices: { id: string; name: string; gender: string; style: string }[] }>('/api/v1/avatars/voices')
        .then((v) => setVoices(v.voices))
        .catch(() => {})

      // Default model: pick based on videoType + whether a reference image is present.
      // Use generationMode (not avatarAssetId) as the avatar/product discriminator to avoid
      // defaulting product-with-ref-image to an avatar model.
      if (!values.modelId && modelsData.items.length > 0) {
        const initVideoType: 'product' | 'avatar' | 'composite' =
          values.videoType ?? (values.generationMode === 'avatar' ? 'avatar' : 'product')
        const isAvatar = initVideoType === 'avatar' || initVideoType === 'composite'
        // Product mode with a reference image → prefer reference-to-video over text-to-video
        const hasProductRef = !isAvatar && values.avatarAssetId !== null
        let defaultModel: VideoModel | undefined

        if (isAvatar) {
          // Avatar / composite mode: default to OmniHuman
          const avatarModels = filterModelsByVideoType(modelsData.items, initVideoType)
          defaultModel =
            avatarModels.find((m) => m.id.includes('omnihuman')) ??
            avatarModels.find((m) => m.id.includes('ai-avatar/v2/pro')) ??
            avatarModels[0]
        } else {
          // Product mode: default to reference-to-video when a ref image is present, t2v otherwise
          const videoModels = filterModelsByVideoType(modelsData.items, 'product')
          if (hasProductRef) {
            defaultModel =
              videoModels.find((m) => m.id === 'bytedance/seedance-2.0/reference-to-video') ??
              videoModels.find((m) => m.id.includes('reference-to-video') && !m.id.includes('fast')) ??
              videoModels.find((m) => m.id.toLowerCase().includes('seedance')) ??
              videoModels[0]
          } else {
            defaultModel =
              videoModels.find((m) => m.id === 'bytedance/seedance-2.0/text-to-video') ??
              videoModels.find((m) => m.id.toLowerCase().includes('seedance')) ??
              videoModels[0]
          }
        }

        if (defaultModel) {
          onChange({
            ...values,
            modelId: defaultModel.id,
            durationSeconds: defaultModel.durations?.[0] ?? 10,
            generationParams: buildDefaultParams(defaultModel),
          })
        }
      }
      setLoading(false)
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof SetupValues>(key: K, val: SetupValues[K]) =>
    onChange({ ...values, [key]: val })

  const toggleArr = (
    key: 'contextFolderIds' | 'styleReferenceIds',
    id: number,
  ) => {
    const arr = values[key]
    onChange({
      ...values,
      [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id],
    })
  }

  const handleModelChange = (modelId: string) => {
    const newModel = models.find((m) => m.id === modelId)
    if (!newModel) return
    const modelARs = newModel.aspect_ratios ?? ['9:16', '1:1', '3:4']
    const currentARValid = modelARs.includes(values.aspectRatio)
    onChange({
      ...values,
      modelId,
      aspectRatio: (currentARValid ? values.aspectRatio : modelARs[0]) as AspectRatio,
      durationSeconds: newModel.durations?.[0] ?? 5,
      generationParams: buildDefaultParams(newModel),
    })
  }

  const setParam = (key: string, val: any) => {
    onChange({ ...values, generationParams: { ...values.generationParams, [key]: val } })
  }

  const canContinue = !!values.modelId && !!values.aspectRatio

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Resolve effective video type: prefer explicit videoType from StepIdea, fall back to avatar flag
  const effectiveVideoType: 'product' | 'avatar' | 'composite' =
    values.videoType ?? (isAvatarMode ? 'avatar' : 'product')

  const visibleModels = filterModelsByVideoType(models, effectiveVideoType)

  const selectedModel = models.find((m) => m.id === values.modelId)
  const modelAspectRatios: string[] = selectedModel?.aspect_ratios?.length
    ? selectedModel.aspect_ratios
    : ['9:16', '1:1', '3:4']
  const userParams = selectedModel?.user_params ?? {}
  const hasAdvancedParams = Object.keys(userParams).length > 0

  // Find selected asset by ID — used in both avatar card and product reference image card
  const selectedAvatarAsset = assets.find((a) => a.id === values.avatarAssetId) ?? null

  // Additional refs the user picked in StepIdea but aren't the primary.
  // The assembly endpoint forwards ALL of these to Seedance (up to 9); we
  // display them here so users can see exactly what's being sent. Without
  // this card, only the primary slot is visible and people assume only one
  // image is going through.
  const extraRefAssets = (values.refImageIds ?? [])
    .filter((id) => id !== values.avatarAssetId)
    .map((id) => assets.find((a) => a.id === id))
    .filter((a): a is Asset => Boolean(a))

  // Pick / clear product reference image and auto-switch model accordingly
  const handleSetRefImage = (asset: Asset | null) => {
    const newId = asset?.id ?? null
    const productModels = filterModelsByVideoType(models, 'product')
    let newModelId = values.modelId
    if (asset && values.modelId.includes('text-to-video')) {
      const refModel =
        productModels.find((m) => m.id === 'bytedance/seedance-2.0/reference-to-video') ??
        productModels.find((m) => m.id.includes('reference-to-video') && !m.id.includes('fast'))
      if (refModel) newModelId = refModel.id
    } else if (!asset && values.modelId.includes('reference-to-video')) {
      const t2vModel =
        productModels.find((m) => m.id === 'bytedance/seedance-2.0/text-to-video') ??
        productModels.find((m) => m.id.includes('text-to-video'))
      if (t2vModel) newModelId = t2vModel.id
    }
    onChange({ ...values, avatarAssetId: newId, modelId: newModelId })
    setShowRefImagePicker(false)
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Production setup</h2>
        <p className="text-sm text-muted-foreground">
          Choose your model, aspect ratio, and optional context assets.
        </p>
      </div>

      {/* ── Fast preview mode (Task 8) ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-1.5">
            <Zap className="h-4 w-4" />
            Fast preview mode
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 cursor-pointer accent-foreground"
              checked={values.fastPreview === true}
              onChange={(e) => set('fastPreview', e.target.checked)}
            />
            <span className="space-y-2">
              <span className="block text-sm font-medium">
                Generate as a quick preview
              </span>
              <ul className="space-y-1">
                {[
                  'Swaps to the fast model variant (when available — roughly half the per-second cost)',
                  'Drops resolution to 480p',
                  'Caps every scene at 5s',
                  'Disables auto-stitch (no final cut)',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <span className="mt-1 shrink-0 h-1 w-1 rounded-full bg-muted-foreground/60" aria-hidden="true" />
                    {line}
                  </li>
                ))}
              </ul>
              {/* Text-only model warning */}
              {selectedModel &&
                selectedModel.mode === 'text-to-video' &&
                selectedModel.accepts_image_input === false && (
                <div className="flex items-start gap-1.5 rounded-md border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-2 text-xs text-yellow-700">
                  <span aria-hidden="true">&#9888;</span>
                  <span>
                    Reference images won&apos;t be passed to this text-only model. Switch to
                    Seedance 2.0 Multimodal (or Fast Multimodal) to keep your refs.
                  </span>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground/80">
                Independent from the &ldquo;Same person across scenes&rdquo; setting on Step 1 — you can use
                Fast preview with bridges ON for a quick low-fidelity bridge-chained run, or OFF
                for the fastest possible parallel preview.
              </p>
            </span>
          </label>
          {values.fastPreview === true && (
            <div className="rounded-md border border-foreground/30 bg-foreground/5 px-3 py-2">
              <p className="text-[11px] text-muted-foreground">
                Preview mode is on. Estimated per-scene latency drops from
                3–10&nbsp;min to roughly 30–60&nbsp;s
                {MODELS_WITH_FAST_VARIANT.has(values.modelId)
                  ? '; per-scene cost roughly halves (fast model variant + 480p)'
                  : '; per-scene cost stays the same (this model has no fast variant — savings come from the 5s scene cap only)'}
                . Switch off before the final render.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Multi-scene stitching (Task 1: multi-scene fix) ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-1.5">
            Final cut
            <HelpHint text="Sequential mode chains scenes via last-frame → first-frame so cuts feel intentional. Auto-stitch combines all scenes into a single MP4 once they finish (color-matched to scene 1 for consistency)." />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 cursor-pointer accent-foreground"
              checked={values.autoStitch !== false}
              onChange={(e) => set('autoStitch', e.target.checked)}
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium">Auto-stitch into one final video</span>
              <span className="block text-xs text-muted-foreground">
                Scenes chain via their last/first frame and get color-matched before stitching.
                Disable to keep them as separate clips.
              </span>
            </span>
          </label>

          {/* Transition smoothness — only relevant when auto-stitching */}
          {values.autoStitch !== false && (
            <div className="space-y-1 pt-1">
              <label className="block text-xs font-medium text-muted-foreground">
                Transition between scenes
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: 0, label: 'Hard cut' },
                  { v: 0.15, label: 'Soft' },
                  { v: 0.3, label: 'Default' },
                  { v: 0.5, label: 'Cinematic' },
                ].map((opt) => {
                  const active = (values.transitionSeconds ?? 0.3) === opt.v
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => set('transitionSeconds', opt.v)}
                      className={cn(
                        'rounded-md border px-2.5 py-1 text-xs transition-colors',
                        active
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border hover:border-foreground/50 text-muted-foreground',
                      )}
                      aria-pressed={active}
                    >
                      {opt.label}{opt.v > 0 ? ` (${opt.v.toFixed(2)}s)` : ''}
                    </button>
                  )
                })}
              </div>
              <p className="text-[11px] text-muted-foreground/80">
                Adds a short crossfade between scenes so the cuts feel
                intentional. Hard cut keeps the original snap.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Reference image (product mode) — always shown, interactive ── */}
      {!isAvatarMode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Reference images
              <Badge variant="outline" className="text-[10px] font-normal">optional</Badge>
              {extraRefAssets.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  · {1 + extraRefAssets.length} attached, all will be sent to the model
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedAvatarAsset ? (
              <div className="flex items-center gap-3">
                <img
                  src={`/uploads/${selectedAvatarAsset.file_path}`}
                  alt={selectedAvatarAsset.original_filename ?? selectedAvatarAsset.filename}
                  className="w-12 h-12 rounded-md object-cover border border-border shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {selectedAvatarAsset.original_filename ?? selectedAvatarAsset.filename}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    This image is the primary reference for the video.
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => setShowRefImagePicker((p) => !p)}
                  >
                    Change
                  </Button>
                  <Button
                    type="button" variant="outline" size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleSetRefImage(null)}
                    aria-label="Remove reference image"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Attach a product image and the model will use it as the visual anchor for the video. Without one, generation is text-only.
                </p>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setShowRefImagePicker((p) => !p)}
                  aria-expanded={showRefImagePicker}
                >
                  <Image className="h-3.5 w-3.5" />
                  Pick from library
                </Button>
              </div>
            )}

            {/* Additional refs from step 1 — read-only strip so users see
                everything they attached, not just the primary. The assembly
                worker sends all of these to Seedance (cap of 9). */}
            {extraRefAssets.length > 0 && (
              <div className="space-y-2 rounded-md border border-dashed border-border p-2.5">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Additional references from step 1
                </p>
                <div className="flex flex-wrap gap-2">
                  {extraRefAssets.map((a) => (
                    <div
                      key={a.id}
                      className="relative w-12 h-12 rounded-md overflow-hidden border border-border"
                      title={a.original_filename ?? a.filename}
                    >
                      <img
                        src={`/uploads/${a.file_path}`}
                        alt={a.original_filename ?? a.filename}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  To add or remove these, go back to step 1.
                </p>
              </div>
            )}

            {showRefImagePicker && (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Select product image</p>
                  <button
                    type="button"
                    onClick={() => setShowRefImagePicker(false)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Close
                  </button>
                </div>
                {assets.filter((a) => a.file_type === 'image').length === 0 ? (
                  <p className="text-sm text-muted-foreground py-3">
                    No images in library.{' '}
                    <a href="/saved-context" target="_blank" rel="noreferrer" className="underline hover:text-foreground">
                      Upload assets
                    </a>
                  </p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-60 overflow-y-auto">
                    {assets.filter((a) => a.file_type === 'image').map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => handleSetRefImage(a)}
                        className={cn(
                          'relative aspect-square rounded overflow-hidden border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground',
                          values.avatarAssetId === a.id
                            ? 'border-foreground ring-1 ring-foreground'
                            : 'border-border hover:border-foreground/50',
                        )}
                        title={a.original_filename ?? a.filename}
                      >
                        <img
                          src={`/uploads/${a.file_path}`}
                          alt={a.original_filename ?? a.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Selected avatar (avatar / composite mode) — read-only ── */}
      {isAvatarMode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Avatar</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedAvatarAsset ? (
              <div className="flex items-center gap-3">
                {selectedAvatarAsset.file_type === 'image' ? (
                  <img
                    src={`/uploads/${selectedAvatarAsset.file_path}`}
                    alt={selectedAvatarAsset.original_filename ?? selectedAvatarAsset.filename}
                    className="w-12 h-12 rounded-md object-cover border border-border"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-md border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">
                    {selectedAvatarAsset.file_type}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {selectedAvatarAsset.original_filename ?? selectedAvatarAsset.filename}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Selected in Step 1. Go back to change.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Avatar asset ID {values.avatarAssetId} not found in library. Go back to Step 1 to reselect.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Aspect ratio + Duration ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-1.5">
            Aspect ratio *
            <HelpHint text="9:16 vertical for TikTok, Reels, Shorts. 4:5 for Instagram feed (portrait). 1:1 square for feed posts. 16:9 for YouTube. 3:4 taller portrait. Note: Seedance has no native 4:5, so 4:5 renders at 3:4 and is auto-cropped to 4:5 after generation." side="right" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            {modelAspectRatios.map((ar) => {
              const arLabels: Record<string, string> = {
                '9:16': 'Portrait / Reels',
                '1:1': 'Square / Feed',
                '3:4': 'Feed / Story',
                '16:9': 'Landscape / YouTube',
                '4:3': 'Classic',
                '4:5': 'Instagram',
                '21:9': 'Ultrawide',
              }
              return (
              <button
                key={ar}
                type="button"
                onClick={() => set('aspectRatio', ar as AspectRatio)}
                className={cn(
                  'flex-1 min-w-[80px] rounded-md border py-3 text-center transition-colors',
                  values.aspectRatio === ar
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border hover:border-foreground/50 text-muted-foreground',
                )}
                aria-pressed={values.aspectRatio === ar}
              >
                <span className="text-sm font-medium">{ar}</span>
                {arLabels[ar] && (
                  <span className="block text-[10px] mt-0.5 opacity-70">{arLabels[ar]}</span>
                )}
              </button>
              )
            })}
          </div>

        </CardContent>
      </Card>

      {/* ── Advanced parameters (collapsible) ── */}
      {hasAdvancedParams && (
        <details className="group rounded-lg border border-border">
          <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm font-medium select-none list-none [&::-webkit-details-marker]:hidden">
            <span className="flex items-center gap-1.5">
              Advanced parameters
              <HelpHint text="Model-specific controls: seed for reproducibility, negative prompt to avoid specific artifacts, and CFG scale for how strictly the model follows the prompt." />
            </span>
            <svg
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="border-t border-border px-4 py-4 space-y-4">
            {Object.entries(userParams).map(([paramKey, def]: [string, UserParamDef]) => {
              const value = values.generationParams[paramKey] ?? def.default ?? null

              if (def.type === 'toggle') {
                return (
                  <div key={paramKey} className="flex items-center justify-between">
                    <Label htmlFor={`param-${paramKey}`} className="text-sm">
                      {def.label}
                    </Label>
                    <Switch
                      id={`param-${paramKey}`}
                      checked={Boolean(value)}
                      onCheckedChange={(checked) => setParam(paramKey, checked)}
                    />
                  </div>
                )
              }

              if (def.type === 'slider') {
                const numVal = value != null ? Number(value) : (def.min ?? 0)
                const sliderHint =
                  paramKey === 'cfg_scale'
                    ? 'How strictly the model follows your prompt. Higher = more literal, can look stiff. Lower = more creative, can drift from intent. Default is usually best.'
                    : undefined
                return (
                  <div key={paramKey} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor={`param-${paramKey}`} className="text-sm">
                          {def.label}
                        </Label>
                        {sliderHint && <HelpHint text={sliderHint} />}
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {numVal}
                      </span>
                    </div>
                    <input
                      id={`param-${paramKey}`}
                      type="range"
                      min={def.min ?? 0}
                      max={def.max ?? 1}
                      step={def.step ?? 0.1}
                      value={numVal}
                      onChange={(e) => setParam(paramKey, Number(e.target.value))}
                      className="w-full h-1.5 appearance-none rounded-full bg-border accent-foreground cursor-pointer"
                      aria-label={def.label}
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>{def.min ?? 0}</span>
                      <span>{def.max ?? 1}</span>
                    </div>
                  </div>
                )
              }

              if (def.type === 'text') {
                const textHint =
                  paramKey === 'negative_prompt'
                    ? "Things to avoid in the output. Seedance responds to 'avoid jitter, avoid bent limbs, avoid temporal flicker, avoid identity drift'. Already set by default — override only if needed."
                    : undefined
                return (
                  <div key={paramKey} className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor={`param-${paramKey}`} className="text-sm">
                        {def.label}
                      </Label>
                      {textHint && <HelpHint text={textHint} />}
                    </div>
                    <Textarea
                      id={`param-${paramKey}`}
                      value={value ?? ''}
                      onChange={(e) => setParam(paramKey, e.target.value || null)}
                      placeholder={def.placeholder}
                      rows={2}
                      className="text-sm resize-none"
                    />
                  </div>
                )
              }

              if (def.type === 'number') {
                const numberHint =
                  paramKey === 'seed'
                    ? 'Lock a specific seed to reproduce a previous generation. Copy the seed from a completed job\'s detail view to iterate on it with tweaked prompts.'
                    : undefined
                return (
                  <div key={paramKey} className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor={`param-${paramKey}`} className="text-sm">
                        {def.label}
                      </Label>
                      {numberHint && <HelpHint text={numberHint} />}
                    </div>
                    <Input
                      id={`param-${paramKey}`}
                      type="number"
                      value={value ?? ''}
                      onChange={(e) =>
                        setParam(paramKey, e.target.value === '' ? null : Number(e.target.value))
                      }
                      placeholder={def.placeholder}
                      className="text-sm w-40"
                    />
                  </div>
                )
              }

              if (def.type === 'select') {
                return (
                  <div key={paramKey} className="space-y-1.5">
                    <Label htmlFor={`param-${paramKey}`} className="text-sm">
                      {def.label}
                    </Label>
                    <Select
                      value={value ?? ''}
                      onValueChange={(v) => setParam(paramKey, v)}
                    >
                      <SelectTrigger id={`param-${paramKey}`} className="w-48">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(def.options ?? []).map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )
              }

              return null
            })}
          </div>
        </details>
      )}

      {/* ── Model picker (filtered by effectiveVideoType / video_types field) ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {effectiveVideoType === 'avatar'
              ? 'Avatar model *'
              : effectiveVideoType === 'composite'
              ? 'Composite model *'
              : 'Video model *'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(() => {
            const seedanceModels = visibleModels.filter((m) => m.company === 'ByteDance')
            const otherModels = visibleModels.filter((m) => m.company !== 'ByteDance')

            if (seedanceModels.length === 0 && otherModels.length === 0) {
              return (
                <p className="text-sm text-muted-foreground">
                  {effectiveVideoType === 'avatar'
                    ? 'No avatar models available yet — backend catalog update pending.'
                    : effectiveVideoType === 'composite'
                    ? 'No composite models available yet — backend catalog update pending.'
                    : 'No models available.'}
                </p>
              )
            }

            const isFastVariant = (id: string) => id.includes('/fast/')
            const renderModelBtn = (m: VideoModel) => (
              <button
                key={m.id}
                type="button"
                onClick={() => handleModelChange(m.id)}
                className={cn(
                  'w-full text-left rounded-md border p-3 transition-colors',
                  values.modelId === m.id
                    ? 'border-foreground bg-accent/30'
                    : 'border-border hover:border-foreground/40',
                )}
                aria-pressed={values.modelId === m.id}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-medium">{m.name}</span>
                    {isFastVariant(m.id) && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-yellow-500/10 text-yellow-700 border-yellow-500/30 shrink-0"
                      >
                        FAST
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {m.quality && (
                      <Badge variant="outline" className="text-[10px]">{m.quality}</Badge>
                    )}
                    {m.price_per_sec != null && (
                      <Badge variant="secondary" className="text-[10px] font-mono">
                        ${m.price_per_sec}/s
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {m.accepts_image_input && (
                    <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      Image required
                    </span>
                  )}
                  {m.accepts_audio_input && (
                    <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      Audio required
                    </span>
                  )}
                  {m.max_duration && (
                    <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      Max {m.max_duration}s
                    </span>
                  )}
                  {m.max_resolution && (
                    <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {m.max_resolution}
                    </span>
                  )}
                </div>
                {m.price_per_sec != null && values.durationSeconds > 0 && values.modelId === m.id && (
                  <p className="text-[11px] text-muted-foreground mt-1.5 font-mono">
                    Est. cost: ${m.price_per_sec}/s × {values.durationSeconds}s = ~${(m.price_per_sec * values.durationSeconds).toFixed(2)}
                  </p>
                )}
                {m.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {m.description}
                  </p>
                )}
              </button>
            )

            const otherGroups = otherModels.reduce<Record<string, VideoModel[]>>((acc, m) => {
              if (!acc[m.company]) acc[m.company] = []
              acc[m.company].push(m)
              return acc
            }, {})

            return (
              <>
                {seedanceModels.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Seedance (ByteDance)
                      </p>
                      <HelpHint text="Seedance 2.0 (ByteDance) is best for product videos. It generates video directly from text + reference images." />
                    </div>
                    <div className="space-y-1.5">
                      {seedanceModels.map(renderModelBtn)}
                    </div>
                  </div>
                )}

                {otherModels.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setShowOtherModels((p) => !p)}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ChevronDown
                          className={cn(
                            'h-3.5 w-3.5 transition-transform',
                            showOtherModels && 'rotate-180',
                          )}
                        />
                        {showOtherModels
                          ? 'Hide other models'
                          : `Show other models (${otherModels.length})`}
                      </button>
                      <HelpHint text="Alternative models: Kling 3.0 (cinematic), OmniHuman 1.5 (talking-head avatars), HeyGen (avatars), Sync Lipsync (lip-sync overlay on existing video). Each has different strengths." side="right" />
                    </div>
                    {showOtherModels && (
                      <div className="mt-3 space-y-4">
                        {Object.entries(otherGroups).map(([company, entries], gi) => (
                          <div key={company}>
                            {gi > 0 && <div className="border-t border-border" />}
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 mt-2">
                              {company}
                            </p>
                            <div className="space-y-1.5">
                              {entries.map(renderModelBtn)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )
          })()}
        </CardContent>
      </Card>

      {/* ── Voice + Audio (avatar mode only) ── */}
      {isAvatarMode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-1.5">
            Voice
            <HelpHint text="Which TTS voice speaks the script for avatar videos. Auto-selects based on avatar's description if left blank (female avatars → female voice, etc)." />
          </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Voice picker — pill buttons grouped by gender */}
            {voices.length > 0 && (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Female voices</p>
                  <div className="flex flex-wrap gap-1.5">
                    {voices.filter((v) => v.gender === 'female').map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => set('voiceId', v.id)}
                        className={cn(
                          'rounded-md border px-2.5 py-1.5 text-xs transition-colors',
                          values.voiceId === v.id
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-border hover:border-foreground/50 text-muted-foreground',
                        )}
                        title={v.style}
                      >
                        {v.name}
                        <span className="ml-1 opacity-60">· {v.style.split(',')[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Male voices</p>
                  <div className="flex flex-wrap gap-1.5">
                    {voices.filter((v) => v.gender === 'male').map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => set('voiceId', v.id)}
                        className={cn(
                          'rounded-md border px-2.5 py-1.5 text-xs transition-colors',
                          values.voiceId === v.id
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-border hover:border-foreground/50 text-muted-foreground',
                        )}
                        title={v.style}
                      >
                        {v.name}
                        <span className="ml-1 opacity-60">· {v.style.split(',')[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {values.voiceId
                    ? `Selected: ${voices.find((v) => v.id === values.voiceId)?.name ?? values.voiceId}`
                    : 'Auto-detected from avatar (default: Bella for female, Adam for male)'
                  }
                </p>
              </div>
            )}

            {/* Optional: custom audio URL override */}
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground flex items-center gap-1.5">
                Or use your own audio file (MP3/WAV)
                <HelpHint text="Optional: paste a URL to a pre-recorded voiceover MP3/WAV. If blank, TTS auto-generates the voice from your script text." side="right" />
              </summary>
              <div className="mt-2 space-y-2">
                <Input
                  id="audioUrl"
                  type="url"
                  placeholder="https://example.com/voice.mp3"
                  value={values.audioUrl}
                  onChange={(e) => set('audioUrl', e.target.value)}
                  className="font-mono text-xs"
                />
                <p className="text-[11px] text-muted-foreground">
                  Overrides TTS — paste a hosted audio URL if you have your own voiceover.
                </p>
              </div>
            </details>
          </CardContent>
        </Card>
      )}

      {/* ── Mood board / lifestyle references (always visible) ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-1.5">
            Mood board / lifestyle references{' '}
            <span className="text-xs font-normal text-muted-foreground ml-1">optional</span>
            <HelpHint text="Optional secondary references — completely separate from the product images you attached in step 1. Use this for brand textures, lifestyle imagery, mood references the AI can pull from as background context. Skip this if your product images alone are enough." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          {folders.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No mood-board folders yet. Create them in Asset Library.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {folders.map((f) => {
                const selected = values.contextFolderIds.includes(f.id)
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleArr('contextFolderIds', f.id)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition-colors',
                      selected
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border hover:border-foreground/50 text-muted-foreground',
                    )}
                    aria-pressed={selected}
                  >
                    {f.name}
                    <Badge
                      variant={selected ? 'outline' : 'secondary'}
                      className={cn(
                        'text-[10px] py-0 px-1',
                        selected && 'border-background/40 text-background',
                      )}
                    >
                      {f.folder_type}
                    </Badge>
                  </button>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Style references (always visible, collapsible) ── */}
      {references.length > 0 && (
        <details className="group rounded-lg border border-border">
          <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm font-medium select-none list-none [&::-webkit-details-marker]:hidden">
            <span className="flex items-center gap-1.5">
              Optional: Style references
              <HelpHint text="Previously-analyzed reference videos whose style (camera movement, lighting, pacing, mood) guides generation. Add reference videos via the Reference Library page." />
            </span>
            <span className="text-xs font-normal text-muted-foreground group-open:hidden">
              Attach reference videos analyzed by AI to steer the look.
            </span>
            <svg
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="border-t border-border px-4 py-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              Attach reference videos analyzed by AI to steer the look.
            </p>
            <div className="flex flex-wrap gap-2">
              {references.map((r) => {
                const selected = values.styleReferenceIds.includes(r.id)
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => toggleArr('styleReferenceIds', r.id)}
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-xs transition-colors max-w-[200px] truncate',
                      selected
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border hover:border-foreground/50 text-muted-foreground',
                    )}
                    aria-pressed={selected}
                    title={r.title}
                  >
                    {r.title}
                  </button>
                )
              })}
            </div>
          </div>
        </details>
      )}

      <Button
        size="lg"
        className="w-full"
        onClick={onNext}
        disabled={!canContinue}
      >
        Continue to Generate
      </Button>
    </div>
  )
}
